import clsx from "clsx";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import Stepper from "components/Stepper";
import { INVITED_CARRIER_DESCRIPTION_LIST } from "constants/haulpay-invited-carrier";
import OnboardingLayout from "layouts/Onboarding";
import { selectOnboardingInformation } from "store/user/selector";
import InvitedCarrierBankInfo from "./BankInfo";
import InvitedCarrierInfo from "./CarrierInfo";
import InvitedCarrierComplete from "./Complete";
import OnboardingIntro from "./Intro";
import styles from "./style.module.scss";

interface ISectionProps {
  step: number;
  setStepCallback: (step: number) => void;
  paymentProfileId: string;
  setPaymentProfileId: React.Dispatch<React.SetStateAction<string>>;
}

function SelectedSection({
  step,
  setStepCallback,
  paymentProfileId,
  setPaymentProfileId
}: Readonly<ISectionProps>) {
  switch (step) {
    case 0:
      return <OnboardingIntro handleNext={(nextStep: number) => setStepCallback(nextStep)} />;

    case 1:
      return (
        <InvitedCarrierInfo
          handleNext={(createdPaymentProfileId) => {
            setPaymentProfileId(createdPaymentProfileId);
            setStepCallback(step + 1);
          }}
        />
      );

    case 2:
      if (!paymentProfileId) {
        return (
          <p role="alert" className="text-center text-error">
            Unable to load your payment profile. Please sign in and try again.
          </p>
        );
      }
      return (
        <InvitedCarrierBankInfo
          handleNext={() => setStepCallback(step + 1)}
          paymentProfileId={paymentProfileId}
        />
      );

    case 3:
      return <InvitedCarrierComplete />;

    default:
      return null;
  }
}

export default function InvitedCarrierOnboarding() {
  const [step, setStep] = useState(0);
  const [paymentProfileId, setPaymentProfileId] = useState("");
  const onboardingInformation = useSelector(selectOnboardingInformation);
  const resolvedPaymentProfileId =
    paymentProfileId || onboardingInformation?.profiles?.payment?.id || "";

  const { title, description } = useMemo(() => INVITED_CARRIER_DESCRIPTION_LIST[step], [step]);

  return (
    <OnboardingLayout className={styles.invitedCarrierContainer}>
      {step ? (
        <>
          <Stepper
            data={Array.from({ length: 3 }).map((_, i) => ({ id: i }))}
            currentStep={step - 1}
            setStep={setStep}
            className="z-20 mt-8"
          />

          <div className={clsx(styles.descriptionWrapper, step === 2 && "text-center gap-4")}>
            <h3 className="text-xl font-medium">{title}</h3>
            <span>{description}</span>
          </div>
        </>
      ) : null}

      <SelectedSection
        step={step}
        setStepCallback={setStep}
        paymentProfileId={resolvedPaymentProfileId}
        setPaymentProfileId={setPaymentProfileId}
      />
    </OnboardingLayout>
  );
}
